import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  HighlightOverride,
  HighlightPin,
  normalizeHighlightOverride,
  normalizeHighlightPin,
} from '@/client/types/highlights';
import { useQuery } from '@tanstack/react-query';

const HIGHLIGHTS = '/api/v2/admin/highlights';

export const highlightsKeys = {
  all: ['highlights-admin'] as const,
  pins: (etid: number) => [...highlightsKeys.all, 'pins', etid] as const,
  override: (etid: number) => [...highlightsKeys.all, 'override', etid] as const,
};

/** ff-api wraps payloads as `{ status, data }`. */
const unwrapEnvelope = (raw: any): any =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw ? raw.data : raw;

const unwrapPinList = (raw: any): any[] => {
  const payload = unwrapEnvelope(raw);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.pins)) return payload.pins;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(raw?.pins)) return raw.pins;
  return [];
};

const useHighlightPinsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (etid: number): Promise<HighlightPin[]> => {
    const raw = await request<any>(`${HIGHLIGHTS}/pin?etid=${etid}`);
    return unwrapPinList(raw).map(normalizeHighlightPin);
  }, 'Failed to fetch highlight pins');
};

export const useHighlightPinsQuery = (etid: number | null) => {
  const queryFn = useHighlightPinsFn();
  return useQuery({
    queryKey: highlightsKeys.pins(etid ?? 0),
    queryFn: () => queryFn(etid!),
    enabled: typeof etid === 'number' && etid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

const useHighlightOverrideFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (etid: number): Promise<HighlightOverride | null> => {
    const raw = await request<any>(`${HIGHLIGHTS}/override?etid=${etid}`);
    const payload = unwrapEnvelope(raw);
    // The API answers `{ overrides: [...] }` (one row when filtered by etid).
    const obj = Array.isArray(payload) ? payload[0] : (payload?.override ?? payload?.overrides?.[0] ?? null);
    return normalizeHighlightOverride(obj);
  }, 'Failed to fetch highlight override');
};

export const useHighlightOverrideQuery = (etid: number | null) => {
  const queryFn = useHighlightOverrideFn();
  return useQuery({
    queryKey: highlightsKeys.override(etid ?? 0),
    queryFn: () => queryFn(etid!),
    enabled: typeof etid === 'number' && etid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/**
 * The category card a slot actually shows.
 *
 * A slot is registered against a pillar (Market, Homes, ...) and a position in
 * that pillar's fan-out, but pins and overrides are matched on the card's own
 * category: the pillar's Nth populated child. Saving against the pillar id
 * changed nothing on the site. This asks the same public endpoint the site
 * uses, so the answer is the card a visitor sees.
 */
export const useSlotCardQuery = (slot: { etid?: number; scope?: 'self' | 'children'; card?: number } | null) => {
  const request = useClientStore((s) => s.request);
  const etid = slot?.etid ?? null;
  const scope = slot?.scope ?? 'self';
  const card = slot?.card ?? 0;
  return useQuery({
    queryKey: ['highlights', 'slot-card', etid, scope, card],
    queryFn: withErrorHandling(async (): Promise<{ etid: number; label: string | null } | null> => {
      if (scope === 'self') return { etid: etid!, label: null };
      const raw = await request<any>(
        `/api/v2/public/entity/highlights?etids=${etid}&scope=children&count=${card + 3}`,
      );
      const hit = unwrapEnvelope(raw)?.cards?.[card];
      return hit?.etid != null ? { etid: Number(hit.etid), label: hit.label ?? hit.title ?? null } : null;
    }, 'Failed to resolve the card for this slot'),
    enabled: typeof etid === 'number' && etid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
